import { Accordion, AccordionDetails, AccordionSummary, Divider, List, ListItem, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageAccessMap, SubMenu } from 'src/Menus';
import { SortableSubmenu } from './SortableSubmenu';
import { NavItem } from './NavItem';
import useNavigation from '../../Providers/NavigationProvider';

interface NavGroupProps {
  label: string;
  subMenu: SubMenu;
  isExpanded: boolean;
  isSortingEnabled: boolean;
  accessKeys: string[];
}

export const NavGroup = ({
  label,
  subMenu,
  isExpanded,
  isSortingEnabled,
  accessKeys,
}: NavGroupProps): React.JSX.Element => {
  const { isOpen, moveEntry, toggleSubMenu } = useNavigation();
  const navigate = useNavigate();

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      moveEntry(subMenu, oldIndex, newIndex);
    },
    [moveEntry, subMenu],
  );

  const items = accessKeys.map((key) => {
    const {
      menu: { icon, label },
      route: { path },
    } = PageAccessMap[key];

    return <NavItem key={key} icon={icon} label={label} onClick={() => navigate(path!)} />;
  });

  // =========================================================
  // Collapsed: flat list of items, no accordion chrome
  // =========================================================
  if (!isOpen) {
    return <>{items}</>;
  }

  // =========================================================
  // Expanded: accordion with optional sortable wrapper
  // =========================================================
  return (
    <ListItem disableGutters disablePadding>
      <Accordion
        square
        disableGutters
        elevation={0}
        expanded={isExpanded}
        onChange={() => toggleSubMenu(subMenu)}
        sx={{ width: '100%' }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ fontWeight: 700, textDecoration: 'none' }}>{label}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          {isSortingEnabled ? (
            <SortableSubmenu items={items} onSortEnd={onSortEnd} lockAxis={'y'} />
          ) : (
            <List disablePadding sx={{ width: '100%' }}>
              {items}
            </List>
          )}
        </AccordionDetails>
        <Divider />
      </Accordion>
    </ListItem>
  );
};
